interface SocialNetwork {
  network: string;
  enabled: boolean;
  url: string;
}

interface FooterData {
  footer_address: string;
  footer_phone: string;
  footer_email: string;
  footer_business_hours: string;
  social_networks: SocialNetwork[];
}

interface FooterEditorProps {
  data: FooterData;
  onChange: (data: FooterData) => void;
}

export default function FooterEditor({ data, onChange }: FooterEditorProps) {
  const handleChange = (field: keyof FooterData, value: string) => {
    onChange({
      ...data,
      [field]: value,
    });
  };

  const handleSocialChange = (index: number, field: keyof SocialNetwork, value: string | boolean) => {
    const newSocials = [...data.social_networks];
    newSocials[index] = {
      ...newSocials[index],
      [field]: value,
    };
    onChange({
      ...data,
      social_networks: newSocials,
    });
  };

  const getNetworkIcon = (network: string) => {
    const icons: { [key: string]: string } = {
      facebook: 'ri-facebook-fill',
      instagram: 'ri-instagram-line',
      tiktok: 'ri-tiktok-fill',
      whatsapp: 'ri-whatsapp-line',
      linkedin: 'ri-linkedin-fill',
      twitter: 'ri-twitter-x-line',
      youtube: 'ri-youtube-fill',
    };
    return icons[network] || 'ri-link';
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <i className="ri-layout-bottom-line text-blue-600"></i>
          Configuración del Footer
        </h2>
        <p className="text-gray-600 mt-1">Información del pie de página y redes sociales</p>
      </div>

      <div className="space-y-6">
        {/* Información Básica */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-information-line text-blue-600"></i>
            Información Básica
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
              <input
                type="text"
                value={data.footer_address}
                onChange={(e) => handleChange('footer_address', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="655 Sebastopol Rd, Santa Rosa, CA 95407"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                <input
                  type="text"
                  value={data.footer_phone}
                  onChange={(e) => handleChange('footer_phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="(707) 369-9830"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={data.footer_email}
                  onChange={(e) => handleChange('footer_email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="mkt@axloweb.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Horario de Atención
              </label>
              <textarea
                value={data.footer_business_hours}
                onChange={(e) => handleChange('footer_business_hours', e.target.value)}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Lunes a Viernes: 9:00 AM - 6:00 PM"
              />
            </div>
          </div>
        </div>

        {/* Redes Sociales */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-share-line text-blue-600"></i>
            Redes Sociales
          </h3>
          <div className="space-y-3">
            {data.social_networks.map((social, index) => (
              <div
                key={social.network}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className={`${getNetworkIcon(social.network)} text-xl text-blue-600`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-gray-900 capitalize">
                          {social.network}
                        </span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={social.enabled}
                            onChange={(e) =>
                              handleSocialChange(index, 'enabled', e.target.checked)
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          <span className="ml-2 text-sm text-gray-600">
                            {social.enabled ? 'Habilitado' : 'Deshabilitado'}
                          </span>
                        </label>
                      </div>
                      <input
                        type="text"
                        value={social.url}
                        onChange={(e) => handleSocialChange(index, 'url', e.target.value)}
                        disabled={!social.enabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 text-sm"
                        placeholder={`https://${social.network}.com/axloweb`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <i className="ri-information-line"></i>
            Nota
          </h3>
          <p className="text-sm text-blue-800">
            Solo las redes sociales habilitadas se mostrarán en el footer del sitio web. Asegúrate
            de ingresar las URLs correctas.
          </p>
        </div>
      </div>
    </div>
  );
}
